import { useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckCircle,
    Trophy,
    Trash,
    ArrowCounterClockwise,
    Flame,
    Play,
} from '@phosphor-icons/react';
import { SESSION } from '@/constants/testIds';
import { WeightStepper } from '@/components/WeightStepper';

const RPE_OPTIONS = ['', '5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];

function readNum(v) {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/**
 * One editable set. Local controlled state mirrors the server value; on edit
 * we debounce-save via PATCH (`onPatch`). On complete the parent calls
 * `onComplete` with the current draft so the server can fire PR detection
 * synchronously.
 */
export function SetRow({ set, units, onPatch, onComplete, onUncomplete, onDelete, onStartPacer, disabled }) {
    const [weight, setWeight] = useState(String(set.weight ?? ''));
    const [reps, setReps] = useState(String(set.reps ?? ''));
    const [rpe, setRpe] = useState(set.rpe == null ? '' : String(set.rpe));
    const [isWarmup, setIsWarmup] = useState(Boolean(set.is_warmup));
    const debounceRef = useRef(null);

    // Re-sync if the server-side set changes underneath us (e.g. PR flag
    // arrives after a completion).
    useEffect(() => {
        setWeight(String(set.weight ?? ''));
        setReps(String(set.reps ?? ''));
        setRpe(set.rpe == null ? '' : String(set.rpe));
        setIsWarmup(Boolean(set.is_warmup));
    }, [set.id, set.weight, set.reps, set.rpe, set.is_warmup]);

    const isCompleted = !!set.completed;
    const isPR = !!set.is_pr;
    const showPrefill = useMemo(() => {
        if (set.prefill_weight == null) return false;
        const n = readNum(weight);
        return n != null && Math.abs(n - set.prefill_weight) < 1e-9;
    }, [weight, set.prefill_weight]);

    function schedulePatch(next) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onPatch(next), 550);
    }

    function onWeightChange(v) {
        setWeight(v);
        schedulePatch({ weight: readNum(v) ?? 0 });
    }
    function onRepsChange(v) {
        setReps(v);
        const n = readNum(v);
        if (n != null) schedulePatch({ reps: Math.max(0, Math.floor(n)) });
    }
    function onRpeChange(v) {
        setRpe(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onPatch({ rpe: v === '' ? null : Number(v) });
    }
    function onWarmupChange() {
        const next = !isWarmup;
        setIsWarmup(next);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onPatch({ is_warmup: next });
    }

    function complete() {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onComplete({
            weight: readNum(weight) ?? 0,
            reps: Math.max(0, Math.floor(readNum(reps) ?? 0)),
            rpe: rpe === '' ? null : Number(rpe),
            is_warmup: isWarmup,
        });
    }

    const rowClasses = isPR
        ? 'border-[#FF4D4D]/40 bg-[#FF4D4D]/5'
        : isCompleted
          ? 'border-[#00FF9D]/35 bg-[#00FF9D]/5'
          : isWarmup
            ? 'border-foreground/10 bg-foreground/[0.02]'
            : 'border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/[0.04]';

    return (
        <li
            data-testid={`${SESSION.setRow}-${set.id}`}
            className={`rounded-2xl border ${rowClasses} px-3 py-3 transition-colors`}
        >
            <div className="flex items-center gap-2 sm:gap-3">
                <span className="font-mono text-xs text-muted-foreground tabular-nums w-7 shrink-0">
                    {isWarmup ? 'W' : String(set.set_number).padStart(2, '0')}
                </span>

                <WeightStepper
                    value={weight}
                    onChange={onWeightChange}
                    units={units}
                    disabled={disabled}
                    setId={set.id}
                />

                <span className="text-muted-foreground">×</span>

                <div className="flex items-baseline gap-1 min-w-0">
                    <input
                        data-testid={`${SESSION.repsInput}-${set.id}`}
                        type="text"
                        inputMode="numeric"
                        value={reps}
                        onChange={(e) => onRepsChange(e.target.value)}
                        disabled={disabled}
                        size={3}
                        aria-label="Reps"
                        className="bg-transparent outline-none font-mono text-xl font-bold tabular-nums w-10 text-right border-b border-transparent focus:border-foreground/40 transition-colors"
                    />
                    <span className="uppercase-mono text-muted-foreground">
                        reps
                    </span>
                </div>

                <select
                    data-testid={`${SESSION.rpeSelect}-${set.id}`}
                    value={rpe}
                    onChange={(e) => onRpeChange(e.target.value)}
                    disabled={disabled}
                    aria-label="RPE"
                    className="hidden sm:block ml-auto sm:ml-0 font-mono text-xs h-8 rounded-lg border border-foreground/15 bg-foreground/5 px-2 outline-none focus:border-foreground/40 text-foreground"
                >
                    {RPE_OPTIONS.map((v) => (
                        <option key={v || 'na'} value={v}>
                            {v === '' ? 'RPE' : `RPE ${v}`}
                        </option>
                    ))}
                </select>

                {isPR && (
                    <span
                        data-testid={`${SESSION.prBadge}-${set.id}`}
                        className="uppercase-mono inline-flex items-center gap-1 rounded-full px-2 py-1 border border-[#FF4D4D]/40 bg-[#FF4D4D]/15 text-[#FF4D4D]"
                    >
                        <Trophy size={12} weight="fill" />
                        PR
                    </span>
                )}

                <div className="ml-auto sm:ml-0 flex items-center gap-1.5">
                    {disabled ? (
                        isCompleted && (
                            <span
                                className="grid place-items-center w-10 h-10 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D]"
                                aria-label="Completed"
                            >
                                <CheckCircle size={18} weight="fill" />
                            </span>
                        )
                    ) : isCompleted ? (
                        <button
                            type="button"
                            onClick={onUncomplete}
                            data-testid={`${SESSION.uncompleteBtn}-${set.id}`}
                            aria-label="Mark set incomplete"
                            className="grid place-items-center w-10 h-10 rounded-full bg-[#00FF9D]/15 border border-[#00FF9D]/40 text-[#00FF9D] hover:bg-[#00FF9D]/25 active:scale-[0.94] transition-all"
                        >
                            <CheckCircle size={18} weight="fill" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={complete}
                            data-testid={`${SESSION.completeBtn}-${set.id}`}
                            aria-label="Mark set complete"
                            className="grid place-items-center w-10 h-10 rounded-full bg-[#00FF9D] text-[#121212] hover:bg-[#00FF9D]/90 active:scale-[0.94] transition-all shadow-neon"
                        >
                            <CheckCircle size={18} weight="bold" />
                        </button>
                    )}
                    {!disabled && !isCompleted && onStartPacer && (
                        <button
                            type="button"
                            onClick={onStartPacer}
                            data-testid={`${SESSION.completeBtn}-${set.id}-pace`}
                            aria-label="Start rep pacer"
                            title="Start rep pacer"
                            className="grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 hover:text-[#00FF9D] active:scale-[0.96] transition-all"
                        >
                            <Play size={14} weight="fill" />
                        </button>
                    )}
                    {!disabled && (
                        <button
                            type="button"
                            onClick={onDelete}
                            data-testid={`${SESSION.deleteSetBtn}-${set.id}`}
                            aria-label="Delete set"
                            className="grid place-items-center w-9 h-9 rounded-xl border border-foreground/10 bg-foreground/5 hover:bg-[#FF4D4D]/15 hover:border-[#FF4D4D]/40 hover:text-[#FF4D4D] active:scale-[0.96] transition-all"
                        >
                            <Trash size={14} weight="bold" />
                        </button>
                    )}
                </div>
            </div>

            {/* Secondary row — warmup + prefill + (mobile) RPE */}
            <div className="mt-2 flex items-center gap-3 flex-wrap pl-9">
                <select
                    data-testid={`${SESSION.rpeSelect}-${set.id}-mobile`}
                    value={rpe}
                    onChange={(e) => onRpeChange(e.target.value)}
                    disabled={disabled}
                    aria-label="RPE"
                    className="sm:hidden font-mono text-xs h-7 rounded-lg border border-foreground/15 bg-foreground/5 px-2 outline-none focus:border-foreground/40 text-foreground"
                >
                    {RPE_OPTIONS.map((v) => (
                        <option key={v || 'na'} value={v}>
                            {v === '' ? 'RPE' : `RPE ${v}`}
                        </option>
                    ))}
                </select>

                {!disabled && (
                    <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={isWarmup}
                            onChange={onWarmupChange}
                            data-testid={`${SESSION.warmupToggle}-${set.id}`}
                            className="accent-[#00FF9D]"
                        />
                        <span className="uppercase-mono text-muted-foreground inline-flex items-center gap-1">
                            <Flame size={11} weight="bold" /> Warm-up
                        </span>
                    </label>
                )}
                {disabled && isWarmup && (
                    <span className="uppercase-mono text-muted-foreground inline-flex items-center gap-1">
                        <Flame size={11} weight="bold" /> Warm-up
                    </span>
                )}

                {showPrefill && (
                    <span
                        data-testid={`${SESSION.prefillBadge}-${set.id}`}
                        title="Pre-filled from your last completed set for this exercise"
                        className="uppercase-mono inline-flex items-center gap-1 text-muted-foreground"
                    >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        from last session
                    </span>
                )}
            </div>
        </li>
    );
}
